import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { complianceAPI } from '../lib/api';
import BiasAuditManager from '../components/BiasAuditManager';

const CATEGORY_LABELS = {
  ada_accommodation: 'ADA Accommodation',
  aedt_alternative: 'AEDT Alternative',
};
const CATEGORY_STYLE = {
  ada_accommodation: { bg: '#EEF2FF', color: '#4338CA' },
  aedt_alternative: { bg: '#FEF3C7', color: '#B45309' },
};
const STATUSES = ['received', 'in_progress', 'resolved', 'rejected'];
const STATUS_STYLE = {
  received: { bg: '#FFFBEB', color: '#B45309' },
  in_progress: { bg: '#FFE7D9', color: '#0076A3' },
  resolved: { bg: '#ECFDF5', color: '#059669' },
  rejected: { bg: '#FEF2F2', color: '#DC2626' },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function CandidateRequestsAdminPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await complianceAPI.getCandidateRequests(categoryFilter, statusFilter);
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error('Failed to load candidate requests');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return requests;
    return requests.filter((r) =>
      (r.name || '').toLowerCase().includes(term) || (r.email || '').toLowerCase().includes(term)
    );
  }, [requests, q]);

  const openCount = useMemo(
    () => requests.filter((r) => r.status !== 'resolved' && r.status !== 'rejected').length,
    [requests]
  );

  const updateStatus = async (id, status) => {
    setSavingId(id);
    try {
      await complianceAPI.updateCandidateRequest(id, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success('Request updated');
    } catch (e) {
      toast.error('Could not update request');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={{ maxWidth: 1150, margin: '0 auto', padding: '2rem 1.5rem' }} data-testid="candidate-requests-admin-page">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 700, color: '#0076A3', margin: 0 }}>Accommodations &amp; AI Requests</h1>
        <p style={{ fontSize: '0.9375rem', color: '#718096', marginTop: '0.375rem' }}>
          ADA accommodation and NYC Local Law 144 alternative-process requests. <strong>{openCount}</strong> open.
        </p>
      </div>

      <BiasAuditManager />

      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <input
            data-testid="cr-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            style={{ flex: '1 1 240px', padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}
          />
          <select data-testid="cr-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}>
            <option value="">All types</option>
            <option value="ada_accommodation">ADA Accommodation</option>
            <option value="aedt_alternative">AEDT Alternative</option>
          </select>
          <select data-testid="cr-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <Button data-testid="cr-refresh-btn" onClick={load} variant="outline" style={{ borderRadius: 10 }}>Refresh</Button>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#A0AEC0' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: '#718096' }} data-testid="cr-empty">No requests found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#A0AEC0', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Received</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Requester</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Type</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Details</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Status</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const cat = CATEGORY_STYLE[r.category] || CATEGORY_STYLE.ada_accommodation;
                  const st = STATUS_STYLE[r.status] || STATUS_STYLE.received;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #F0EBE3' }} data-testid="cr-row">
                      <td style={{ padding: '0.75rem 0.5rem', color: '#4A5568', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <div style={{ fontWeight: 600, color: '#0076A3' }}>{r.name || '—'}</div>
                        <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{r.email}</div>
                        {r.job_title && <div style={{ fontSize: '0.625rem', color: '#CBD5E1' }}>{r.job_title}</div>}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: 999, background: cat.bg, color: cat.color }}>{CATEGORY_LABELS[r.category] || r.category}</span>
                        {r.context && <div style={{ fontSize: '0.625rem', color: '#CBD5E1', marginTop: '0.25rem' }}>{r.context}</div>}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', maxWidth: 280, color: '#4A5568' }}>{r.details || '—'}</td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: 999, background: st.bg, color: st.color, textTransform: 'capitalize' }}>{(r.status || '').replace('_', ' ')}</span>
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <select
                          data-testid={`cr-status-select-${r.id}`}
                          value={r.status}
                          disabled={savingId === r.id}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          style={{ padding: '0.35rem 0.5rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.75rem', cursor: 'pointer' }}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
